import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { stationTheme } from "@sisuwellness/web-components";

const StyleType = {
    primary: "Primary",
    secondary: "Secondary"
};

const ButtonContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
`;

const HelperLabel = styled.div`
    font-size: 0.9rem;
    color: ${props => props.theme.colours.flat.blue.hex};
    margin-top: 0.25rem;
    line-height: 1.2;
    text-transform: none;
`;

const StyledButton = styled.button`
    /* Responsive font sizing */
    font-size: clamp(1rem, 2.5vw, 1.8rem);
    font-weight: ${props => props.theme.actionButtons.text.weight};

    white-space: normal;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px 12px;
    line-height: 1.2;

    /* Centering */
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    /* Height */
    height: ${props => props.theme.actionButtons.height};

    /* Existing styles */
    border-radius: ${props => props.theme.actionButtons.borderRadius};
    filter: ${props => props.theme.actionButtons.shadow.normal};
    user-select: none;
    text-transform: uppercase;
    position: relative;
    z-index: 1;

    :focus {
        outline: none;
    }

    :disabled {
        background-color: ${props => props.theme.colours.flat.gray.hex};
    }
`;

const StyledPrimaryButton = styled(StyledButton)`
    background-color: ${props => props.theme.actionButtons.primary.backgroundColor.normal};
    color: ${props => props.theme.actionButtons.primary.color.normal};
    border: ${props => props.theme.actionButtons.primary.border.normal};

    :active {
        background-color: ${props => props.theme.actionButtons.primary.backgroundColor.active};
        filter: ${props => props.theme.actionButtons.shadow.active};
    }
`;

const StyledSecondaryButton = styled(StyledButton)`
    background-color: ${props => props.theme.actionButtons.secondary.backgroundColor.normal};
    color: ${props => props.theme.actionButtons.secondary.color.normal};
    border: ${props => props.theme.actionButtons.secondary.border.normal};

    :active {
        color: ${props => props.theme.actionButtons.secondary.color.active};
        border: ${props => props.theme.actionButtons.secondary.border.active};
        filter: ${props => props.theme.actionButtons.shadow.active};
    }

    :disabled {
        color: ${props => props.theme.colours.flat.white.hex};
        border-width: 0;
    }
`;

class ActionButton extends Component {
    static propTypes = {
        label: PropTypes.string.isRequired,
        helperLabel: PropTypes.string,
        styleType: PropTypes.string,
        onClick: PropTypes.func,
        maxWidth: PropTypes.string,
        maxHeight: PropTypes.string,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        "data-testid": PropTypes.string
    };

    static defaultProps = {
        styleType: "Primary"
    };

    onClick(e) {
        if (this.props.onClick !== undefined) {
            this.props.onClick(e);
        }
    }

    renderButtonContent() {
        return (
            <ButtonContent>
                <span>{this.props.label}</span>
                {this.props.helperLabel && <HelperLabel>{this.props.helperLabel}</HelperLabel>}
            </ButtonContent>
        );
    }

    render() {
        let button;
        switch (this.props.styleType) {
            case "Primary":
                button = (
                    <StyledPrimaryButton
                        className={`action-button-${this.props.className}`}
                        onClick={e => this.onClick(e)}
                        disabled={this.props.disabled}
                        data-testid={this.props["data-testid"]}
                    >
                        {this.renderButtonContent()}
                    </StyledPrimaryButton>
                );
                break;

            case "Secondary":
                button = (
                    <StyledSecondaryButton
                        className={`action-button-sec-${this.props.className}`}
                        onClick={e => this.onClick(e)}
                        disabled={this.props.disabled}
                        data-testid={this.props["data-testid"]}
                    >
                        {this.renderButtonContent()}
                    </StyledSecondaryButton>
                );
                break;

            default:
                button = (
                    <StyledPrimaryButton
                        className={`action-button-${this.props.className}`}
                        onClick={e => this.onClick(e)}
                        disabled={this.props.disabled}
                        data-testid={this.props["data-testid"]}
                    >
                        {this.renderButtonContent()}
                    </StyledPrimaryButton>
                );
                break;
        }

        return button;
    }
}

const StyledActionButton = styled(ActionButton)`
    margin: ${props => props.margin || 0};
    padding: ${props => props.padding || 0};
    max-height: ${props => (props.maxHeight ? props.maxHeight : stationTheme.actionButtons.height)};
    max-width: ${props => (props.maxWidth ? props.maxWidth : stationTheme.actionButtons.width)};
    width: 100%;
    height: 100%;
`;

export { StyledActionButton as default, StyleType, StyledSecondaryButton };
